import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import portalsApi from '../api/portalsApi';
import adsApi from '../api/adsApi';
import Spinner from '../components/spinner';

const AdComponent = ({ ad }) => {
  if (!ad || !ad.is_active) return null;
  
  return (
    <div className="w-full h-40 bg-white dark:bg-gray-800 rounded-lg shadow-sm my-8">
      <a 
        href={ad.link} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="block w-full h-full"
      >
        <img 
          src={ad.image} 
          alt={ad.title} 
          className="w-full h-full object-contain"
        />
      </a>
    </div>
  );
};

const PortalPage = () => {
  const { portalId } = useParams();
  const [portal, setPortal] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ads, setAds] = useState({});

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await adsApi.getAll();
        if (response.data.status === 'success') {
          const processedAds = {};
          Object.entries(response.data.data).forEach(([key, adList]) => {
            processedAds[key] = adList.map(ad => ({
              ...ad,
              image: ad.image.startsWith('http') ? ad.image : `${process.env.REACT_APP_API_URL}${ad.image}`
            }));
          });
          setAds(processedAds);
        }
      } catch (err) {
        console.error("Error fetching ads:", err);
      }
    };
    fetchAds();
  }, []);

  useEffect(() => {
    const fetchPortal = async () => {
      setIsLoading(true);
      try {
        const response = await portalsApi.getById(portalId);
        setPortal(response.data);
      } catch (err) {
        setError('Failed to load portal data');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (portalId) {
      fetchPortal();
    }
  }, [portalId]);

  if (isLoading) return <Spinner />;
  if (error) return <div className="text-red-500">{error}</div>;
  if (!portal) return <div>Portal not found</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">{portal.name}</h1>
      
      {/* Top Ad */}
      <AdComponent ad={ads.portal_top?.[0]} />

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {portal.articles?.map((article, index) => {
          // Show ads after every 6 articles
          const shouldShowAd = (index + 1) % 6 === 0;
          const adPosition = `portal_between_articles_${Math.floor((index + 1) / 6)}`;
          
          return (
            <React.Fragment key={article.id}>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                {/* Article content */}
                <h2 className="text-xl font-semibold mb-4">{article.title}</h2>
                <p className="text-gray-600 dark:text-gray-300">{article.excerpt}</p>
              </div>
              
              {/* Show ad after every 6th article */}
              {shouldShowAd && (
                <div className="col-span-full">
                  <AdComponent ad={ads[adPosition]?.[0]} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default PortalPage;
